import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, Receipt, Eye, X, Plus, Trash2, Calendar,
  Truck, DollarSign, Save, Package, CreditCard, FileText, RotateCcw,
  ChevronDown, Layers, Edit3, Printer,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import PurchaseInvoice from '../components/PurchaseInvoice';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'credit', label: 'Credit (Pay Later)' },
  { value: 'personal_account', label: 'Personal Account' },
];

export default function Purchases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [medPagination, setMedPagination] = useState({ page: 1, pages: 1, total: 0, loading: false });
  const [medSearching, setMedSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [viewPurchase, setViewPurchase] = useState(null);
  const [printPurchase, setPrintPurchase] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [showRefund, setShowRefund] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', phone: '', address: '' });
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [activeTab, setActiveTab] = useState('purchases');

  const [form, setForm] = useState({
    reference_number: '',
    supplier_id: '',
    purchase_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash',
    payment_status: 'paid',
    paid_amount: '',
    tax: '0',
    notes: '',
    items: [emptyItem()],
  });

  const [refundForm, setRefundForm] = useState({
    supplier_id: '', purchase_id: '',
    refund_date: new Date().toISOString().slice(0, 10),
    refund_type: 'return', refund_amount: '',
    medicine_id: '', return_quantity: '', reason: '', notes: '',
  });
  const [refunds, setRefunds] = useState([]);

  function emptyItem() {
    return {
      medicine_id: '',
      batch_number: '',
      mf_date: '',
      expiry_date: '',
      unit_name: '',
      conversion_factor: 1,
      quantity: 1,
      buy_price: '',
      sell_price: '',
      discount: '0',
      availableUnits: [],
    };
  }

  const getMedicineUnits = (med) => {
    if (!med) return [];
    if (Array.isArray(med.units) && med.units.length > 0) {
      return med.units;
    }
    return [
      {
        id: 0,
        unit_name: med.unit || 'pcs',
        level: 1,
        child_count: 1,
        conversion_factor: 1,
        cost_price: parseFloat(med.avg_cost_price) || 0,
        selling_price: parseFloat(med.selling_price) || 0,
        is_base_unit: true,
      },
    ];
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [purRes, supRes, medRes] = await Promise.all([
        api.get('/purchases?limit=500'),
        api.get('/suppliers?limit=500').catch(() => ({ suppliers: [] })),
        api.get('/medicines?limit=200').catch(() => ({ medicines: [] })),
      ]);
      setPurchases(Array.isArray(purRes) ? purRes : (purRes?.purchases || []));
      setSuppliers(Array.isArray(supRes) ? supRes : (supRes?.suppliers || []));
      const meds = Array.isArray(medRes) ? medRes : (medRes?.medicines || []);
      setMedicines(meds);
      if (medRes?.pagination) {
        setMedPagination({
          page: medRes.pagination.page || 1,
          pages: medRes.pagination.pages || 1,
          total: medRes.pagination.total || meds.length,
          loading: false,
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load data');
    }
  };

  const loadMoreMedicines = async () => {
    if (medPagination.loading || medPagination.page >= medPagination.pages) return;
    setMedPagination((prev) => ({ ...prev, loading: true }));
    try {
      const nextPage = medPagination.page + 1;
      const res = await api.get(`/medicines?page=${nextPage}&limit=200`);
      const newMeds = Array.isArray(res) ? res : (res?.medicines || []);
      if (newMeds.length > 0) {
        setMedicines((prev) => {
          const map = new Map(prev.map((m) => [String(m.id), m]));
          for (const m of newMeds) {
            map.set(String(m.id), m);
          }
          return Array.from(map.values());
        });
      }
      setMedPagination({
        page: nextPage,
        pages: res?.pagination?.pages || medPagination.pages,
        total: res?.pagination?.total || medPagination.total,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to load more medicines', err);
      setMedPagination((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSearchMedicines = async (query) => {
    const q = (query || '').trim();
    if (!q) return;
    setMedSearching(true);
    try {
      const res = await api.get(`/medicines?search=${encodeURIComponent(q)}&limit=100`);
      const foundMeds = Array.isArray(res) ? res : (res?.medicines || []);
      if (foundMeds.length > 0) {
        setMedicines((prev) => {
          const map = new Map(prev.map((m) => [String(m.id), m]));
          for (const m of foundMeds) {
            map.set(String(m.id), m);
          }
          return Array.from(map.values());
        });
      }
    } catch (err) {
      console.error('Failed to search medicines', err);
    } finally {
      setMedSearching(false);
    }
  };

  const loadRefunds = async () => {
    try {
      const res = await api.get('/supplier-refunds?limit=500');
      setRefunds(Array.isArray(res) ? res : (res?.refunds || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load refunds');
    }
  };

  useEffect(() => { if (activeTab === 'refunds') loadRefunds(); }, [activeTab]);

  const generatePoNumber = () => {
    const d = new Date();
    const dateStr = d.toISOString().slice(0, 10).replace(/-/g, '');
    return `PO-${dateStr}-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  const openCreateModal = () => {
    setEditingPurchaseId(null);
    setForm({
      reference_number: generatePoNumber(),
      supplier_id: '',
      purchase_date: new Date().toISOString().slice(0, 10),
      payment_method: 'cash',
      payment_status: 'paid',
      paid_amount: '',
      tax: '0',
      notes: '',
      items: [emptyItem()],
    });
    setShowCreate(true);
  };

  const openEditModal = async (pur) => {
    try {
      let data = pur;
      try {
        const fullPur = await api.get(`/purchases/${pur.id}`);
        if (fullPur) data = fullPur;
      } catch {
        // Fallback to pur if single fetch fails
      }

      setEditingPurchaseId(data.id);

      // Ensure all medicines in data.items are present in medicines state
      const missingMedIds = (data.items || [])
        .map((it) => String(it.medicine_id))
        .filter((id) => id && !medicines.some((m) => String(m.id) === id));

      let allMeds = medicines;
      if (missingMedIds.length > 0) {
        try {
          const fetchedMeds = await Promise.all(
            missingMedIds.map((id) => api.get(`/medicines/${id}`).catch(() => null))
          );
          const validFetched = fetchedMeds.filter(Boolean);
          if (validFetched.length > 0) {
            const map = new Map(allMeds.map((m) => [String(m.id), m]));
            for (const m of validFetched) {
              map.set(String(m.id), m);
            }
            allMeds = Array.from(map.values());
            setMedicines(allMeds);
          }
        } catch (err) {
          console.warn('Could not fetch missing purchase medicines', err);
        }
      }

      const mappedItems = (data.items || []).map((it) => {
        const med = allMeds.find((m) => String(m.id) === String(it.medicine_id));
        const units = getMedicineUnits(med);
        const chosenUnit = units.find((u) => u.unit_name === it.unit_name) || units[0] || {
          unit_name: it.unit_name || med?.unit || 'pcs',
          conversion_factor: 1,
        };

        return {
          medicine_id: String(it.medicine_id),
          batch_number: it.batch_number || '',
          mf_date: it.mf_date ? it.mf_date.slice(0, 10) : '',
          expiry_date: it.expiry_date ? it.expiry_date.slice(0, 10) : '',
          unit_name: it.unit_name || chosenUnit.unit_name,
          conversion_factor: parseInt(it.conversion_factor) || chosenUnit.conversion_factor || 1,
          quantity: it.unit_quantity || it.quantity || 1,
          buy_price: it.buy_price || '',
          sell_price: it.sell_price || '',
          discount: it.discount || '0',
          availableUnits: units,
        };
      });

      setForm({
        reference_number: data.reference_number || '',
        supplier_id: data.supplier_id ? String(data.supplier_id) : '',
        purchase_date: data.purchase_date ? data.purchase_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
        payment_method: data.payment_method || 'cash',
        payment_status: data.payment_status || (data.payment_method === 'credit' ? 'unpaid' : 'paid'),
        paid_amount: data.paid_amount !== undefined && data.paid_amount !== null ? String(data.paid_amount) : '',
        tax: data.tax || '0',
        notes: data.notes || '',
        items: mappedItems.length > 0 ? mappedItems : [emptyItem()],
      });
      if (viewPurchase) setViewPurchase(null);
      setShowCreate(true);
    } catch (err) {
      toast.error(err.message || 'Failed to load purchase details for editing');
    }
  };

  const handlePrintPurchase = async (pur) => {
    try {
      const fullPur = await api.get(`/purchases/${pur.id}`);
      setPrintPurchase(fullPur || pur);
    } catch {
      setPrintPurchase(pur);
    }
  };

  const handlePaymentMethodChange = (method) => {
    const isCredit = method === 'credit';
    const isPersonal = method === 'personal_account';
    setForm((prev) => ({
      ...prev,
      payment_method: method,
      payment_status: isCredit ? 'unpaid' : 'paid',
      paid_amount: isCredit ? '0' : (isPersonal ? '' : prev.paid_amount),
    }));
  };

  const handlePaidAmountChange = (val) => {
    const pAmt = parseFloat(val);
    let newStatus = form.payment_status;
    if (!isNaN(pAmt)) {
      if (pAmt >= grandTotal && grandTotal > 0) {
        newStatus = 'paid';
      } else if (pAmt > 0) {
        newStatus = 'partial';
      } else {
        newStatus = 'unpaid';
      }
    }
    setForm((prev) => ({
      ...prev,
      paid_amount: val,
      payment_status: newStatus,
    }));
  };

  const handlePaymentStatusChange = (status) => {
    setForm((prev) => ({
      ...prev,
      payment_status: status,
      paid_amount: status === 'paid' ? String(grandTotal) : status === 'unpaid' ? '0' : prev.paid_amount,
    }));
  };

  const addItemRow = () => setForm((p) => ({ ...p, items: [...p.items, emptyItem()] }));

  const removeItemRow = (idx) => {
    if (form.items.length <= 1) { toast.error('At least one item required'); return; }
    setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
  };

  const handleItemChange = (idx, field, value) => {
    setForm((prev) => {
      const newItems = [...prev.items];
      newItems[idx] = { ...newItems[idx], [field]: value };
      if (field === 'medicine_id') {
        const med = medicines.find((m) => String(m.id) === String(value));
        if (med) {
          const units = getMedicineUnits(med);
          const topUnit = units[0] || { unit_name: med.unit || 'pcs', conversion_factor: 1, cost_price: 0, selling_price: 0 };
          newItems[idx].availableUnits = units;
          newItems[idx].unit_name = topUnit.unit_name;
          newItems[idx].conversion_factor = topUnit.conversion_factor || 1;
          newItems[idx].buy_price = topUnit.cost_price > 0 ? topUnit.cost_price : (med.avg_cost_price || '');
          newItems[idx].sell_price = topUnit.selling_price > 0 ? topUnit.selling_price : (med.selling_price || '');
        } else {
          newItems[idx].availableUnits = [];
          newItems[idx].unit_name = '';
          newItems[idx].conversion_factor = 1;
          newItems[idx].buy_price = '';
          newItems[idx].sell_price = '';
        }
      }
      return { ...prev, items: newItems };
    });
  };

  const handleUnitChange = (idx, unitName) => {
    setForm((prev) => {
      const newItems = [...prev.items];
      const it = newItems[idx];
      const med = medicines.find((m) => String(m.id) === String(it.medicine_id));
      const units = it.availableUnits && it.availableUnits.length > 0 ? it.availableUnits : getMedicineUnits(med);
      const selectedU = units.find((u) => u.unit_name === unitName);
      if (selectedU) {
        newItems[idx] = {
          ...it,
          availableUnits: units,
          unit_name: selectedU.unit_name,
          conversion_factor: selectedU.conversion_factor || 1,
          buy_price: selectedU.cost_price > 0 ? selectedU.cost_price : (it.buy_price || ''),
          sell_price: selectedU.selling_price > 0 ? selectedU.selling_price : (it.sell_price || ''),
        };
      } else {
        newItems[idx] = {
          ...it,
          unit_name: unitName,
        };
      }
      return { ...prev, items: newItems };
    });
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) { toast.error('Supplier name is required'); return; }
    try {
      const res = await api.post('/suppliers', newSupplier);
      const created = { id: res.id || res.supplier_id, ...newSupplier };
      setSuppliers((prev) => [...prev, created]);
      setForm((prev) => ({ ...prev, supplier_id: String(created.id) }));
      setNewSupplier({ name: '', phone: '', address: '' });
      setShowAddSupplier(false);
      toast.success('Supplier added');
    } catch (err) { toast.error(err.message || 'Failed to add supplier'); }
  };

  const handleDeletePurchase = async (purchaseId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order? This will also revert stocked inventory batches and supplier balance.')) {
      return;
    }
    try {
      await api.delete(`/purchases/${purchaseId}`);
      toast.success('Purchase order deleted');
      if (viewPurchase?.id === purchaseId) {
        setViewPurchase(null);
      }
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase order');
    }
  };

  const calcRowDiscount = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buy_price) || 0;
    const discPercent = parseFloat(item.discount) || 0;
    return (qty * price * discPercent) / 100;
  };

  const calcRowTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buy_price) || 0;
    const discPercent = parseFloat(item.discount) || 0;
    const gross = qty * price;
    const discAmount = (gross * discPercent) / 100;
    return gross - discAmount;
  };

  const grossSubtotal = form.items.reduce((sum, it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.buy_price) || 0;
    return sum + (qty * price);
  }, 0);
  const itemsDiscount = form.items.reduce((sum, it) => sum + calcRowDiscount(it), 0);
  const subtotal = grossSubtotal;
  const taxAmount = parseFloat(form.tax) || 0;
  const grandTotal = Math.max(0, grossSubtotal - itemsDiscount + taxAmount);

  const getCalculatedPayment = () => {
    if (form.payment_method === 'personal_account') {
      return { paidAmount: grandTotal, paymentStatus: 'paid', balanceDue: 0 };
    }
    if (form.payment_method === 'credit') {
      return { paidAmount: 0, paymentStatus: 'unpaid', balanceDue: grandTotal };
    }
    if (form.paid_amount !== '' && !isNaN(parseFloat(form.paid_amount))) {
      const p = Math.min(grandTotal, Math.max(0, parseFloat(form.paid_amount)));
      const status = p >= grandTotal && grandTotal > 0 ? 'paid' : p > 0 ? 'partial' : 'unpaid';
      const bal = Math.max(0, grandTotal - p);
      return { paidAmount: p, paymentStatus: status, balanceDue: bal };
    }
    if (form.payment_status === 'unpaid') {
      return { paidAmount: 0, paymentStatus: 'unpaid', balanceDue: grandTotal };
    }
    return { paidAmount: grandTotal, paymentStatus: 'paid', balanceDue: 0 };
  };

  const { paidAmount, paymentStatus: activePaymentStatus, balanceDue } = getCalculatedPayment();

  const handleSavePurchase = async (e) => {
    e.preventDefault();
    if (!form.reference_number.trim()) { toast.error('Invoice number is required'); return; }
    if (form.items.some((it) => !it.medicine_id || (parseFloat(it.quantity) || 0) <= 0)) {
      toast.error('All items need a valid medicine and quantity'); return;
    }
    try {
      const payload = {
        reference_number: form.reference_number.trim(),
        supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
        user_id: user?.id ? parseInt(user.id) : 1,
        purchase_date: form.purchase_date,
        payment_method: form.payment_method,
        subtotal,
        discount: itemsDiscount,
        tax: taxAmount,
        total: grandTotal,
        payment_status: activePaymentStatus,
        paid_amount: paidAmount,
        balance_due: balanceDue,
        notes: form.notes || null,
        items: form.items.map((it) => ({
          medicine_id: parseInt(it.medicine_id),
          batch_number: it.batch_number || null,
          mf_date: it.mf_date || null,
          expiry_date: it.expiry_date || null,
          unit_name: it.unit_name || null,
          unit_quantity: parseInt(it.quantity) || 1,
          conversion_factor: parseInt(it.conversion_factor) || 1,
          quantity: (parseInt(it.quantity) || 1) * (parseInt(it.conversion_factor) || 1),
          buy_price: parseFloat(it.buy_price) || 0,
          sell_price: parseFloat(it.sell_price) || 0,
          discount: parseFloat(it.discount) || 0,
        })),
      };

      if (editingPurchaseId) {
        await api.put(`/purchases/${editingPurchaseId}`, payload);
        toast.success('Purchase order updated');
      } else {
        await api.post('/purchases', payload);
        if (form.payment_method === 'personal_account') {
          toast.success('Stock adjusted via personal purchase successfully');
        } else {
          toast.success('Purchase order created');
        }
      }

      setShowCreate(false);
      setEditingPurchaseId(null);
      loadData();
    } catch (err) { toast.error(err.message || 'Failed to save purchase'); }
  };

  const handleViewPurchase = async (pur) => {
    try { const res = await api.get(`/purchases/${pur.id}`); setViewPurchase(res); }
    catch { setViewPurchase(pur); }
  };

  const handleSaveRefund = async (e) => {
    e.preventDefault();
    if (!refundForm.supplier_id) { toast.error('Supplier is required'); return; }
    if (refundForm.refund_type === 'return' && (!refundForm.medicine_id || !refundForm.return_quantity)) {
      toast.error('Medicine and return quantity are required'); return;
    }
    if (refundForm.refund_type === 'overpayment' && (!refundForm.refund_amount || parseFloat(refundForm.refund_amount) <= 0)) {
      toast.error('Refund amount is required'); return;
    }
    try {
      const payload = {
        ...refundForm,
        user_id: user?.id ? parseInt(user.id) : 1,
        refund_amount: refundForm.refund_type === 'overpayment'
          ? parseFloat(refundForm.refund_amount)
          : (() => {
              const med = medicines.find((m) => String(m.id) === String(refundForm.medicine_id));
              const unitCost = parseFloat(med?.avg_cost_price) || parseFloat(med?.cost_price) || parseFloat(med?.selling_price) || 0;
              return unitCost * (parseInt(refundForm.return_quantity) || 0);
            })(),
      };
      await api.post('/supplier-refunds', payload);
      toast.success('Supplier refund processed');
      setShowRefund(false);
      setRefundForm({
        supplier_id: '', purchase_id: '',
        refund_date: new Date().toISOString().slice(0, 10),
        refund_type: 'return', refund_amount: '',
        medicine_id: '', return_quantity: '', reason: '', notes: '',
      });
      loadData(); loadRefunds();
    } catch (err) { toast.error(err.message || 'Failed to process refund'); }
  };

  const filtered = purchases.filter((p) => {
    const matchSearch = (p.reference_number || '').toLowerCase().includes(search.toLowerCase())
      || (p.supplier_name || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || p.payment_status === filterStatus;
    let matchDate = true;
    if (dateFilter.from) matchDate = matchDate && p.purchase_date >= dateFilter.from;
    if (dateFilter.to) matchDate = matchDate && p.purchase_date <= dateFilter.to;
    return matchSearch && matchStatus && matchDate;
  });

  const totalPurchases = filtered.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
  const outstanding = filtered.filter((p) => p.payment_status !== 'paid')
    .reduce((sum, p) => sum + ((parseFloat(p.total) || 0) - (parseFloat(p.paid_amount) || 0)), 0);

  const statusColors = {
    paid: 'bg-emerald-100 text-emerald-700',
    partial: 'bg-amber-100 text-amber-700',
    unpaid: 'bg-red-100 text-red-700',
  };

  const statusLabels = {
    paid: 'Paid',
    partial: 'Partial',
    unpaid: 'Pending (Unpaid)',
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const selectedSupplier = suppliers.find((s) => String(s.id) === String(form.supplier_id));
  const selectedRefundSupplier = suppliers.find((s) => String(s.id) === String(refundForm.supplier_id));

  const supplierOptions = suppliers.map((s) => {
    const due = Number(s.balance || 0);
    const advance = Number(s.credit_balance || 0);
    const balanceParts = [];
    if (advance > 0) balanceParts.push(`Advance: Rs. ${advance.toLocaleString('en-PK', { minimumFractionDigits: 2 })}`);
    if (due > 0) balanceParts.push(`Due: Rs. ${due.toLocaleString('en-PK', { minimumFractionDigits: 2 })}`);
    const balanceText = balanceParts.length > 0 ? balanceParts.join(' | ') : (s.phone ? `Phone: ${s.phone}` : null);

    return {
      value: String(s.id),
      label: s.name,
      sublabel: s.phone && balanceParts.length > 0 ? `${s.phone} • ${balanceText}` : balanceText,
    };
  });
  const medicineOptions = medicines.map((m) => {
    const subDetails = [
      m.generic_name ? `Generic: ${m.generic_name}` : null,
      m.barcode ? `Barcode: ${m.barcode}` : null,
      m.sku ? `SKU: ${m.sku}` : null,
    ].filter(Boolean).join(' • ');

    return {
      value: String(m.id),
      label: m.name,
      sublabel: subDetails || null,
      keywords: `${m.name} ${m.generic_name || ''} ${m.barcode || ''} ${m.sku || ''} ${m.manufacturer || ''}`,
    };
  });

  return (
    <div className="space-y-6">
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-white p-1 w-fit">
        {[
          { key: 'purchases', label: 'Purchases', icon: Receipt },
          { key: 'refunds', label: 'Supplier Refunds', icon: RotateCcw },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={clsx('flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors',
              activeTab === tab.key ? 'bg-emerald-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50')}>
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'purchases' && (<>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Purchases</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">Rs. {totalPurchases.toFixed(2)}</p>
            <p className="text-xs text-gray-400 mt-1">{filtered.length} orders</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Outstanding</p>
            <p className="text-2xl font-bold text-red-600 mt-1">Rs. {outstanding.toFixed(2)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Suppliers</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{suppliers.length}</p>
          </div>
        </div>

        <DateFilter
          dateFrom={dateFilter.from}
          dateTo={dateFilter.to}
          onDateFromChange={(val) => setDateFilter({ ...dateFilter, from: val })}
          onDateToChange={(val) => setDateFilter({ ...dateFilter, to: val })}
          onApply={() => {}}
          onReset={() => setDateFilter({ from: '', to: '' })}
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reference or supplier..."
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
          </div>
          <div className="flex items-center gap-1.5">
            {[
              { key: 'all', label: 'All' },
              { key: 'paid', label: 'Paid' },
              { key: 'partial', label: 'Partial' },
              { key: 'unpaid', label: 'Pending (Unpaid)' },
            ].map((s) => (
              <button key={s.key} onClick={() => setFilterStatus(s.key)}
                className={clsx('rounded-lg px-3 py-2 text-xs font-semibold capitalize transition-colors',
                  filterStatus === s.key ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50')}>
                {s.label}
              </button>
            ))}
          </div>
          <button onClick={openCreateModal}
            className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 active:scale-[0.97]">
            <Plus className="h-4 w-4" /> New Purchase
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Invoice #</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Supplier</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Method</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Total</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Paid</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((pur) => (
                  <tr key={pur.id} className="group transition-colors hover:bg-gray-50/50">
                    <td className="sticky-col-left px-6 py-4 whitespace-nowrap"><span className="font-mono text-sm font-semibold text-gray-900">{pur.reference_number}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">{pur.supplier_name || 'General'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{fmtDate(pur.purchase_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                      <span className={clsx('px-2 py-0.5 rounded text-xs font-semibold', pur.payment_method === 'credit' ? 'bg-amber-50 text-amber-800' : 'text-gray-700')}>
                        {pur.payment_method === 'personal_account' ? 'Personal Account' : (pur.payment_method || 'cash')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Rs. {(parseFloat(pur.total) || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">Rs. {(parseFloat(pur.paid_amount) || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                        statusColors[pur.payment_status] || 'bg-gray-100 text-gray-700')}>
                        {statusLabels[pur.payment_status] || pur.payment_status}
                      </span>
                    </td>
                    <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleViewPurchase(pur)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors" title="View Details">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => openEditModal(pur)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" title="Edit Purchase Order">
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button onClick={() => handlePrintPurchase(pur)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-purple-50 hover:text-purple-600 transition-colors" title="Print Invoice / PDF">
                          <Printer className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeletePurchase(pur.id)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete Purchase Order">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 space-y-3">
              <Receipt className="h-12 w-12 text-gray-300" />
              <p className="text-base font-medium text-gray-600">No purchase orders found</p>
              <button onClick={openCreateModal}
                className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-600">
                <Plus className="h-4 w-4" /> New Purchase Order
              </button>
            </div>
          )}
        </div>
      </>)}

      {activeTab === 'refunds' && (<>
        <div className="flex justify-end">
          <button onClick={() => setShowRefund(true)}
            className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 active:scale-[0.97]">
            <RotateCcw className="h-4 w-4" /> New Supplier Refund
          </button>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Supplier</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Type</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Amount</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {refunds.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-400">No refunds recorded</td></tr>
                )}
                {refunds.map((r) => (
                  <tr key={r.id} className="group transition-colors hover:bg-gray-50/50">
                    <td className="sticky-col-left px-6 py-4 whitespace-nowrap text-sm text-gray-600">{fmtDate(r.refund_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">{r.supplier_name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                        r.refund_type === 'return' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}>
                        {r.refund_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">Rs. {(parseFloat(r.refund_amount) || 0).toFixed(2)}</td>
                    <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.reason || r.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {showCreate && createPortal(
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-8 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowCreate(false); setEditingPurchaseId(null); }} />
          <div className="relative w-full max-w-5xl rounded-2xl border border-gray-200 bg-white shadow-2xl mb-8">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingPurchaseId ? 'Edit Purchase Order' : 'New Purchase Order'}
                </h3>
                <p className="text-xs text-gray-500">
                  {editingPurchaseId ? 'Update purchase details, unit conversions, and inventory items' : 'Fill in purchase details, select units, and add inventory items'}
                </p>
              </div>
              <button onClick={() => { setShowCreate(false); setEditingPurchaseId(null); }} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSavePurchase} className="px-6 py-5 space-y-6">

              {/* ── PURCHASE INFO ── */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700">
                    <FileText className="h-4 w-4 text-emerald-500" /> Purchase Information
                  </p>
                  {selectedSupplier && (
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1 rounded-lg">
                        <span className="text-[10px] uppercase font-bold text-emerald-600">Advance:</span>
                        Rs. {Number(selectedSupplier.credit_balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-red-50 text-red-800 border border-red-200 px-3 py-1 rounded-lg">
                        <span className="text-[10px] uppercase font-bold text-red-600">Due Amount:</span>
                        Rs. {Number(selectedSupplier.balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Supplier</label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SearchableSelect
                          value={form.supplier_id}
                          onChange={(val) => setForm({ ...form, supplier_id: val })}
                          options={supplierOptions}
                          placeholder="-- Select Supplier --"
                          searchPlaceholder="Search supplier..."
                        />
                      </div>
                      <button type="button" onClick={() => setShowAddSupplier(true)}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                        title="Add new supplier">
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {selectedSupplier && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                          <span className="text-[10px] font-bold uppercase text-emerald-600">Adv:</span>
                          <strong>Rs. {Number(selectedSupplier.credit_balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-800">
                          <span className="text-[10px] font-bold uppercase text-red-600">Due:</span>
                          <strong>Rs. {Number(selectedSupplier.balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Invoice Number *</label>
                    <input value={form.reference_number}
                      onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                      placeholder="PO-20260820-1234"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none" required />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Purchase Date *</label>
                    <input type="date" value={form.purchase_date}
                      onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none" required />
                  </div>
                  <div className="flex items-end">
                    <button type="button" onClick={addItemRow}
                      className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-600 hover:bg-emerald-100 transition-colors">
                      <Plus className="h-4 w-4" /> Add Row
                    </button>
                  </div>
                </div>
              </div>

              {/* ── INVENTORY ITEMS ── */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 mb-3">
                  <Package className="h-4 w-4 text-emerald-500" /> Inventory Items ({form.items.length})
                </p>
                {medicines.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-center justify-between">
                    <span>No medicines available.</span>
                    <button type="button" onClick={() => { setShowCreate(false); navigate('/medicines/new'); }}
                      className="font-bold underline text-amber-900">Add Medicine First</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {form.items.map((item, idx) => {
                      const rowTotal = calcRowTotal(item);
                      const med = medicines.find((m) => String(m.id) === String(item.medicine_id));
                      const baseUnitName = med?.unit || 'units';
                      const factor = parseInt(item.conversion_factor) || 1;
                      const qtyNumber = parseInt(item.quantity) || 0;
                      const restockBaseQty = qtyNumber * factor;

                      return (
                        <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-xs p-3.5 space-y-3">
                          {/* Top Row: Medicine (wide), Batch No, MF Date, Expiry Date */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 items-end">
                            <div className="sm:col-span-2 lg:col-span-2">
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Medicine *</label>
                              <SearchableSelect
                                value={item.medicine_id}
                                onChange={(val) => handleItemChange(idx, 'medicine_id', val)}
                                options={medicineOptions}
                                placeholder="-- Select Medicine --"
                                searchPlaceholder="Search medicine name, generic, barcode..."
                                size="sm"
                                onSearch={handleSearchMedicines}
                                onLoadMore={loadMoreMedicines}
                                hasMore={medPagination.page < medPagination.pages}
                                loading={medSearching || medPagination.loading}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Batch No</label>
                              <input
                                value={item.batch_number}
                                onChange={(e) => handleItemChange(idx, 'batch_number', e.target.value)}
                                placeholder="e.g. BN-001"
                                className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                              <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">MF Date</label>
                                <input
                                  type="date"
                                  value={item.mf_date}
                                  onChange={(e) => handleItemChange(idx, 'mf_date', e.target.value)}
                                  className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1.5 text-[11px] text-gray-900 focus:border-emerald-500 focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Expiry Date</label>
                                <input
                                  type="date"
                                  value={item.expiry_date}
                                  onChange={(e) => handleItemChange(idx, 'expiry_date', e.target.value)}
                                  className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1.5 text-[11px] text-gray-900 focus:border-emerald-500 focus:outline-none"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Middle Row: Unit Dropdown, Qty, Buy Price, Sell Price, Discount */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2.5 items-end pt-2 border-t border-gray-100">
                            <div>
                              <label className="block text-[10px] font-bold text-emerald-800 uppercase mb-1 flex items-center gap-1">
                                <Layers className="h-3 w-3 text-emerald-600" /> Purchase Unit *
                              </label>
                              <select
                                value={item.unit_name || ''}
                                onChange={(e) => handleUnitChange(idx, e.target.value)}
                                disabled={!item.medicine_id}
                                className="w-full rounded-md border border-emerald-300 bg-emerald-50/50 px-2.5 py-1.5 text-xs font-bold text-emerald-900 focus:border-emerald-500 focus:outline-none disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 cursor-pointer"
                              >
                                {item.availableUnits && item.availableUnits.length > 0 ? (
                                  item.availableUnits.map((u) => (
                                    <option key={u.unit_name} value={u.unit_name}>
                                      {u.unit_name} {u.conversion_factor > 1 ? `(1=${u.conversion_factor})` : '(Base)'}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">Select Medicine First</option>
                                )}
                              </select>
                            </div>

                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                Qty ({item.unit_name || 'Units'}) *
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                                className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-bold text-gray-900 focus:border-emerald-500 focus:outline-none"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                Buy Price / {item.unit_name || 'Unit'} *
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.buy_price}
                                onChange={(e) => handleItemChange(idx, 'buy_price', e.target.value)}
                                placeholder="0.00"
                                className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900 focus:border-emerald-500 focus:outline-none"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                Sell Price / {item.unit_name || 'Unit'}
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.sell_price}
                                onChange={(e) => handleItemChange(idx, 'sell_price', e.target.value)}
                                placeholder="0.00"
                                className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none"
                              />
                            </div>

                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Discount (%)</label>
                              <input
                                type="number"
                                step="any"
                                min="0"
                                max="100"
                                value={item.discount}
                                onChange={(e) => handleItemChange(idx, 'discount', e.target.value)}
                                placeholder="0%"
                                className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none"
                              />
                            </div>
                          </div>

                          {/* Row Footer: Restocking calculation & Row total */}
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100 flex-wrap gap-2">
                            <div className="flex items-center gap-2 text-xs">
                              {item.medicine_id && item.unit_name && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                  <Package className="h-3 w-3 text-emerald-600" />
                                  Restocks: <strong>{restockBaseQty} {baseUnitName}</strong>
                                  {factor > 1 && (
                                    <span className="text-gray-500 font-normal ml-1">
                                      (1 {item.unit_name} = {factor} {baseUnitName})
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 ml-auto">
                              <p className="text-xs font-medium text-gray-500">
                                Row Total: <span className="text-sm font-bold text-gray-900">Rs. {rowTotal.toFixed(2)}</span>
                              </p>
                              <button
                                type="button"
                                onClick={() => removeItemRow(idx)}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                                title="Remove row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── PAYMENT + SUMMARY ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* PAYMENT DETAILS */}
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-4">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700">
                    <CreditCard className="h-4 w-4 text-emerald-500" /> Payment Details
                  </p>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method</label>
                    <SearchableSelect
                      value={form.payment_method}
                      onChange={(val) => handlePaymentMethodChange(val)}
                      options={PAYMENT_METHODS.map((m) => ({ value: m.value, label: m.label }))}
                      placeholder="Select Payment Method"
                      searchPlaceholder="Search method..."
                    />
                  </div>
                  {form.payment_method === 'personal_account' && (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 space-y-1">
                      <p className="font-bold flex items-center gap-1.5 text-emerald-800">
                        <Package className="h-3.5 w-3.5 text-emerald-600" /> Personal Account Purchase
                      </p>
                      <p className="text-[11px] leading-relaxed text-emerald-700">
                        This order is paid from personal funds. Inventory quantities and stock valuation will be updated directly, but this purchase will not appear in purchase history, supplier dues, or business expense calculations.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Status</label>
                    <select
                      disabled={form.payment_method === 'personal_account'}
                      value={form.payment_method === 'personal_account' ? 'paid' : form.payment_status}
                      onChange={(e) => handlePaymentStatusChange(e.target.value)}
                      className={clsx(
                        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 focus:border-emerald-500 focus:outline-none",
                        form.payment_method === 'personal_account' && "bg-gray-100 cursor-not-allowed text-gray-500"
                      )}
                    >
                      <option value="paid">{form.payment_method === 'personal_account' ? 'Paid (Personal Funds)' : 'Paid (Fully Settled)'}</option>
                      <option value="partial">Partial Payment</option>
                      <option value="unpaid">Pending (Unpaid / Credit)</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Amount Paid (Rs.)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={grandTotal}
                      disabled={form.payment_method === 'personal_account'}
                      value={form.payment_method === 'personal_account' ? grandTotal.toFixed(2) : form.paid_amount}
                      onChange={(e) => handlePaidAmountChange(e.target.value)}
                      placeholder={form.payment_method === 'credit' ? '0.00' : grandTotal.toFixed(2)}
                      className={clsx(
                        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none",
                        form.payment_method === 'personal_account' && "bg-gray-100 cursor-not-allowed text-gray-500"
                      )}
                    />
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3 flex justify-between">
                    <span className="text-sm font-semibold text-gray-600">Balance Due:</span>
                    <span className={clsx('text-sm font-bold', balanceDue > 0 ? 'text-red-600' : 'text-emerald-600')}>
                      Rs. {balanceDue.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Additional Notes</label>
                    <textarea rows={2} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Invoice notes, shipment terms..."
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none" />
                  </div>
                </div>

                {/* SUMMARY */}
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 flex flex-col justify-between">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 mb-3">
                    <DollarSign className="h-4 w-4 text-emerald-500" /> Summary
                  </p>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal:</span>
                      <span className="font-semibold text-gray-900">Rs. {subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Discount:</span>
                      <span className="font-semibold text-amber-600">- Rs. {itemsDiscount.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-600">
                      <span>Tax / Freight:</span>
                      <div className="w-28">
                        <input type="number" step="0.01" min="0" value={form.tax}
                          onChange={(e) => setForm({ ...form, tax: e.target.value })}
                          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-right text-xs font-semibold focus:border-emerald-500 focus:outline-none" />
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-2.5 flex justify-between">
                      <span className="text-base font-bold text-gray-900">Grand Total:</span>
                      <span className="text-base font-bold text-emerald-700">Rs. {grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  <button type="submit" disabled={medicines.length === 0}
                    className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm active:scale-[0.98]">
                    <Save className="h-4 w-4" /> {editingPurchaseId ? 'UPDATE PURCHASE ORDER' : 'COMPLETE PURCHASE'}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => { setShowCreate(false); setEditingPurchaseId(null); }}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {showAddSupplier && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowAddSupplier(false)} />
          <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Supplier</h3>
              <button onClick={() => setShowAddSupplier(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Name *</label>
                <input value={newSupplier.name} onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:bg-white" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label>
                <input value={newSupplier.phone} onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:bg-white" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                <textarea rows={2} value={newSupplier.address}
                  onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:bg-white" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowAddSupplier(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleAddSupplier}
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600">Add Supplier</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewPurchase && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewPurchase(null)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Purchase Details</h3>
                <p className="font-mono text-xs text-gray-500">{viewPurchase.reference_number}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => openEditModal(viewPurchase)}
                  className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
                  title="Edit Purchase Order"
                >
                  <Edit3 className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => {
                    handlePrintPurchase(viewPurchase);
                  }}
                  className="flex items-center gap-1 rounded-lg bg-purple-50 px-2.5 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100 transition-colors"
                  title="Print Invoice / PDF"
                >
                  <Printer className="h-3.5 w-3.5" /> Print
                </button>
                <button
                  onClick={() => handleDeletePurchase(viewPurchase.id)}
                  className="flex items-center gap-1 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors"
                  title="Delete Purchase Order"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setViewPurchase(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 ml-1">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Supplier</span>
                <span className="font-semibold text-gray-900">{viewPurchase.supplier_name || 'General'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Date</span>
                <span className="text-gray-900">{fmtDate(viewPurchase.purchase_date)}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Payment Method</span>
                <span className={clsx('font-semibold uppercase', viewPurchase.payment_method === 'credit' ? 'text-amber-700' : 'text-gray-900')}>
                  {viewPurchase.payment_method === 'personal_account' ? 'Personal Account' : (viewPurchase.payment_method || 'cash')}
                </span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Total</span>
                <span className="font-bold text-gray-900">Rs. {(parseFloat(viewPurchase.total) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Paid</span>
                <span className="text-gray-900 font-medium">Rs. {(parseFloat(viewPurchase.paid_amount) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Balance Due</span>
                <span className={clsx('font-bold', (parseFloat(viewPurchase.balance_due) || 0) > 0 ? 'text-red-600' : 'text-emerald-600')}>
                  Rs. {(parseFloat(viewPurchase.balance_due) || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Status</span>
                <span className={clsx('font-semibold capitalize',
                  viewPurchase.payment_status === 'paid' ? 'text-emerald-600'
                  : viewPurchase.payment_status === 'partial' ? 'text-amber-600' : 'text-red-600')}>
                  {statusLabels[viewPurchase.payment_status] || viewPurchase.payment_status}
                </span>
              </div>
              {viewPurchase.notes && (
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Notes</span>
                  <span className="text-gray-900">{viewPurchase.notes}</span>
                </div>
              )}
              {viewPurchase.items && viewPurchase.items.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Items Ordered</p>
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 space-y-2 text-xs">
                    {viewPurchase.items.map((it, idx) => {
                      const uQty = it.unit_quantity || it.quantity;
                      const uName = it.unit_name || it.base_unit || 'units';
                      const factor = parseInt(it.conversion_factor) || 1;
                      const totalBaseUnits = factor > 1 ? uQty * factor : null;

                      return (
                        <div key={idx} className="flex items-center justify-between py-1.5 border-b border-gray-200/60 last:border-0">
                          <div>
                            <p className="text-gray-900 font-bold">
                              {it.medicine_name} <span className="text-emerald-700 font-semibold">x{uQty} {uName}</span>
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {it.batch_number ? `Batch: ${it.batch_number} • ` : ''}
                              Rs. {(parseFloat(it.buy_price) || 0).toFixed(2)} / {uName}
                              {parseFloat(it.discount) > 0 ? ` • Disc: ${parseFloat(it.discount)}%` : ''}
                              {totalBaseUnits ? ` (${totalBaseUnits} ${it.base_unit || 'base units'} total)` : ''}
                            </p>
                          </div>
                          <span className="font-bold text-gray-900 text-sm">
                            Rs. {(parseFloat(it.total) || (uQty * (parseFloat(it.buy_price) || 0))).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {printPurchase && createPortal(
        <PurchaseInvoice
          purchase={printPurchase}
          onClose={() => setPrintPurchase(null)}
        />,
        document.body
      )}

      {showRefund && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRefund(false)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Supplier Refund</h3>
                <p className="text-xs text-gray-500">Process a return or overpayment refund</p>
              </div>
              <button onClick={() => setShowRefund(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveRefund} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Supplier *</label>
                  <SearchableSelect
                    value={refundForm.supplier_id}
                    onChange={(val) => setRefundForm({ ...refundForm, supplier_id: val })}
                    options={supplierOptions}
                    placeholder="-- Select Supplier --"
                    searchPlaceholder="Search supplier..."
                    required
                  />
                  {selectedRefundSupplier && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                        <span className="text-[10px] font-bold uppercase text-emerald-600">Adv:</span>
                        <strong>Rs. {Number(selectedRefundSupplier.credit_balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-800">
                        <span className="text-[10px] font-bold uppercase text-red-600">Due:</span>
                        <strong>Rs. {Number(selectedRefundSupplier.balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Refund Date</label>
                  <input type="date" value={refundForm.refund_date}
                    onChange={(e) => setRefundForm({ ...refundForm, refund_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Refund Type</label>
                <div className="flex gap-2">
                  {['return', 'overpayment'].map((t) => (
                    <button type="button" key={t} onClick={() => setRefundForm({ ...refundForm, refund_type: t })}
                      className={clsx('flex-1 py-2 text-xs font-semibold rounded-lg capitalize border transition-colors',
                        refundForm.refund_type === t
                          ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100')}>
                      {t === 'return' ? 'Goods Return' : 'Overpayment Refund'}
                    </button>
                  ))}
                </div>
              </div>
              {refundForm.refund_type === 'return' && (<>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Medicine *</label>
                    <SearchableSelect
                      value={refundForm.medicine_id}
                      onChange={(val) => setRefundForm({ ...refundForm, medicine_id: val })}
                      options={medicineOptions}
                      placeholder="-- Select Medicine --"
                      searchPlaceholder="Search medicine name, generic, barcode..."
                      onSearch={handleSearchMedicines}
                      onLoadMore={loadMoreMedicines}
                      hasMore={medPagination.page < medPagination.pages}
                      loading={medSearching || medPagination.loading}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Return Qty *</label>
                    <input type="number" min="1" value={refundForm.return_quantity}
                      onChange={(e) => setRefundForm({ ...refundForm, return_quantity: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                  </div>
                </div>
              </>)}
              {refundForm.refund_type === 'overpayment' && (
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Refund Amount (Rs.) *</label>
                  <input type="number" step="0.01" min="0" value={refundForm.refund_amount}
                    onChange={(e) => setRefundForm({ ...refundForm, refund_amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                </div>
              )}
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Reason</label>
                <input value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                  placeholder="e.g. Damaged goods, expired items..."
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                <textarea rows={2} value={refundForm.notes}
                  onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => setShowRefund(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit"
                  className="flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-600">
                  <RotateCcw className="h-4 w-4" /> Process Refund
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
